import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

// Define supported languages
export const SUPPORTED_LANGUAGES = ['en', 'ja'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// ── Pre-sync URL ?lang= to localStorage BEFORE i18n.init() ──
// The i18next LanguageDetector should detect ?lang=ja from the querystring,
// but for users who already have i18nextLng=en in localStorage (returning visitors),
// there's a race condition where the cached value can win over the URL param.
// By writing the URL language to localStorage before init, both querystring AND
// localStorage agree on the language, eliminating the race entirely.
if (typeof window !== 'undefined') {
  const urlParams = new URLSearchParams(window.location.search);
  const urlLang = urlParams.get('lang');
  if (urlLang && SUPPORTED_LANGUAGES.includes(urlLang as SupportedLanguage)) {
    localStorage.setItem('i18nextLng', urlLang);
  }
}

// All application namespaces (full list for authenticated routes)
export const ALL_NAMESPACES = [
  'common', 'dashboard', 'profile', 'integrations', 'meetingTypes', 'booking',
  'forms', 'contacts', 'availability', 'teams', 'billing', 'communication',
  'admin', 'analytics', 'onboarding', 'affiliate', 'payments',
  'products', 'polls', 'shop',
] as const;

// Namespaces needed for public pages (booking page, cancel, reschedule, and
// answering a poll — poll respondents are guests with no account)
const PUBLIC_NAMESPACES = ['common', 'booking', 'availability', 'forms', 'polls', 'shop'];

// Namespaces visible immediately on the first authenticated page (chrome + schedule cards):
//   dashboard / profile  — DashboardLayout, Navbar, ProfileDropdown
//   meetingTypes         — schedule-page cards and filters
//   billing              — DashboardLayout upgrade button on Free plan
// Loaded eagerly when entering an authenticated route so JA users don't see
// English-fallback text flash on first paint.
const AUTH_CRITICAL_NAMESPACES = ['dashboard', 'profile', 'meetingTypes', 'billing'];

// Language display names
export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  ja: '日本語'
};

// Initialize i18next
i18n
  .use(Backend) // Load translations from backend/files
  .use(LanguageDetector) // Detect user language
  .use(initReactI18next) // Pass i18n instance to react-i18next
  .init({
    // Debug mode - set to false in production
    debug: false, // Disable debug to prevent console errors in production
    
    // Default language
    fallbackLng: 'en',
    
    // Supported languages
    supportedLngs: SUPPORTED_LANGUAGES,
    
    // Language detection options
    detection: {
      // Order of language detection methods
      // querystring first so external sites (e.g. marketing website) can pass ?lang=ja
      order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],

      // Cache user language
      caches: ['localStorage'],

      // localStorage key
      lookupLocalStorage: 'i18nextLng',

      // URL query parameter key (e.g. ?lang=ja)
      lookupQuerystring: 'lang',

      // Don't lookup from path, subdomain, etc.
      lookupFromPathIndex: 0,
      lookupFromSubdomainIndex: 0,

      // Exclude these from detection
      excludeCacheFor: ['cimode']
    },
    
    // Backend configuration
    backend: {
      // Path to load resources from
      loadPath: '/locales/{{lng}}/{{ns}}.json',
      // Add timeout and retry for production
      requestOptions: {
        cache: 'default'
      },
      // Disable backend loading in production as a fallback
      allowMultiLoading: false
    },
    
    // Default namespace
    defaultNS: 'common',
    
    // Only preload public namespaces at startup (common + booking + availability + forms).
    // This keeps the public booking page fast (4 files instead of 15).
    // Authenticated routes preload critical namespaces eagerly via preloadCriticalAuthNamespaces()
    // and the rest via preloadDeferredAuthNamespaces() (idle).
    // When adding a new namespace file, add it to ALL_NAMESPACES above.
    ns: PUBLIC_NAMESPACES,
    
    // Interpolation options
    interpolation: {
      // React already escapes by default
      escapeValue: false,
      
      // Formatting functions
      format: (value, format, lng) => {
        if (format === 'lowercase') return value.toLowerCase();
        if (format === 'uppercase') return value.toUpperCase();
        
        // Date formatting based on language
        if (format === 'date' && value instanceof Date) {
          return new Intl.DateTimeFormat(lng).format(value);
        }
        
        if (format === 'datetime' && value instanceof Date) {
          return new Intl.DateTimeFormat(lng, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }).format(value);
        }
        
        return value;
      }
    },
    
    // React specific options
    react: {
      // Don't wait for translations to avoid blocking render
      useSuspense: false,
      
      // Bind i18n instance to React
      bindI18n: 'languageChanged',
      bindI18nStore: 'added',
      
      // Transition options
      transEmptyNodeValue: '',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'em', 'span']
    },
    
    // Load missing keys to help development (disabled to prevent React render errors)
    saveMissing: false,
    
    // Handle missing translations gracefully.
    // Only log warnings after all namespaces have finished loading,
    // to avoid noisy false positives during async namespace loading on page load.
    parseMissingKeyHandler: (_key: string, defaultValue?: string) => {
      return defaultValue || _key;
    },
    
    // Pluralization
    pluralSeparator: '_',
    contextSeparator: '_',
    
    // Return keys when missing translations in development
    returnEmptyString: false,
    returnNull: false,
    returnObjects: false
  })
  .then(() => {
  })
  .catch((error) => {
  });

// Helper function to change language
export const changeLanguage = (lng: SupportedLanguage) => {
  return i18n.changeLanguage(lng);
};

// Helper function to get current language
export const getCurrentLanguage = (): SupportedLanguage => {
  const currentLng = i18n.language;
  return SUPPORTED_LANGUAGES.includes(currentLng as SupportedLanguage) 
    ? currentLng as SupportedLanguage 
    : 'en';
};

// Helper function to check if language is supported
export const isLanguageSupported = (lng: string): lng is SupportedLanguage => {
  return SUPPORTED_LANGUAGES.includes(lng as SupportedLanguage);
};

/**
 * Preload namespaces visible on the first authenticated page (chrome + schedule cards).
 * Call eagerly when entering an authenticated route — these strings render immediately
 * so JA users would otherwise see English fallbacks flash before the file loads.
 * Uses i18n.loadNamespaces() which deduplicates and skips already-loaded namespaces.
 */
let criticalNamespacesPreloaded = false;
export const preloadCriticalAuthNamespaces = () => {
  if (criticalNamespacesPreloaded) return;
  criticalNamespacesPreloaded = true;
  i18n.loadNamespaces(AUTH_CRITICAL_NAMESPACES)
    .catch(() => {
      criticalNamespacesPreloaded = false; // Allow retry on failure
    });
};

/**
 * Preload the remaining namespaces for authenticated routes (integrations, contacts,
 * teams, communication, admin, analytics, onboarding) — needed when the user navigates
 * to other sections. Call from `requestIdleCallback` so it runs after first paint and
 * doesn't compete with the API fan-out for sockets / main-thread time.
 * Uses i18n.loadNamespaces() which deduplicates and skips already-loaded namespaces.
 */
let deferredNamespacesPreloaded = false;
export const preloadDeferredAuthNamespaces = () => {
  if (deferredNamespacesPreloaded) return;
  deferredNamespacesPreloaded = true;
  const remaining = ALL_NAMESPACES.filter(
    (ns) => !PUBLIC_NAMESPACES.includes(ns) && !AUTH_CRITICAL_NAMESPACES.includes(ns)
  );
  i18n.loadNamespaces([...remaining])
    .catch(() => {
      deferredNamespacesPreloaded = false; // Allow retry on failure
    });
};

export default i18n;